import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ICostinglevelArticleMappingLayout } from '../../../models/layout/costinglevel-article-mapping-layout.model';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { CostinglevelArticleMappingLayoutService } from '../../../services/layout/costinglevel-article-mapping-layout.service';
import { distinctUntilChanged, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostingLevelArticleNr } from '../../../models/costing-level-article.model';
import { CostinglevelArticleMappingFacade } from '../../../services/facades/external-data/costinglevel-article-mapping-facade';
import {
    ICostingLevelArticleMappingState,
} from '../../../services/facades/external-data/costinglevel-article-mapping.state.model';

@Component({
    templateUrl: './costinglevel-article-mapping.page.html',
    styleUrls: [ './costinglevel-article-mapping.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CostinglevelArticleMappingPage implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort)
    public sort: MatSort;

    @ViewChild(MatPaginator)
    public paginator: MatPaginator;

    public dataSource: MatTableDataSource<ICostingLevelArticleNr> = new MatTableDataSource<ICostingLevelArticleNr>([]);
    public mappingState$: Observable<ICostingLevelArticleMappingState>;
    public sharedDataState$: Observable<ISharedDataState>;
    public layout: ICostinglevelArticleMappingLayout;
    public costingLevelSelectionFormControl: FormControl<number> = new FormControl<number>(undefined);

    private destroy$: Subject<void> = new Subject();

    constructor(private myCostinglevelArticleMappingFacade: CostinglevelArticleMappingFacade,
                private myCostinglevelArticleMappingLayoutService: CostinglevelArticleMappingLayoutService,
                private mySharedDataFacade: SharedDataFacade) {
    }

    public ngOnInit(): void {
        this.layout = this.myCostinglevelArticleMappingLayoutService.getCostinglevelArticleMappingLayout();
        this.mappingState$ = this.myCostinglevelArticleMappingFacade.subState();
        this.mappingState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ICostingLevelArticleMappingState) => s.mappings),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: ICostingLevelArticleNr[]) => {
                this.dataSource.data = data;
            });

        this.sharedDataState$ = this.mySharedDataFacade.subState();

        void firstValueFrom(this.mySharedDataFacade.fetchCostingLevels());

        void this.myCostinglevelArticleMappingFacade.fetchMappings();

        this.dataSource.sortingDataAccessor = (mapping: ICostingLevelArticleNr, tableColumnName: string): string | number => {
            switch (tableColumnName) {
                case 'costingLevelName':
                    return mapping.costingLevel.name;
                default:
                    return mapping[ tableColumnName ];
            }
        };

        this.dataSource.filterPredicate = ( costLevelArt: ICostingLevelArticleNr, filter: string ): boolean => {
            const filterValues = filter.trim().toLowerCase();
            // filter on costLevelArt.costingLevel.name or costLevelArt.articleNr
            if ( costLevelArt.articleNr.toLowerCase().includes(filterValues) || costLevelArt.costingLevel.name.toLowerCase().includes(filterValues) ) {
                return true;
            } else {
                return false;
            }
        };
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        // Remove Sales from memory
        this.myCostinglevelArticleMappingFacade.clearState();
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.costingLevelSelectionFormControl.valueChanges.subscribe((costingLevelId: number ) => {
            void this.myCostinglevelArticleMappingFacade.fetchMappings(costingLevelId);
        });
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;

        if ( this.dataSource.paginator){
            this.dataSource.paginator.firstPage();
        }
    }

}
